import React, { JSX, useEffect, useMemo, useState } from 'react';
import { Time, HoursOfOperation } from 'components/types/LocationTypes';
import moment from 'moment';

type LocationHoursProps = {
  hours: string;
  hoursNotes?: string;
};

type LocationTime = Time & {
  isClosed?: boolean;
};

type LocationStatus = {
  className: string;
  label: string;
};

const dayOrder = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const LocationHours = (props: LocationHoursProps): JSX.Element => {
  const { hours, hoursNotes } = props;
  const [currentDate, setCurrentDate] = useState<Date | null>(null);

  const jsonHours = useMemo<Array<HoursOfOperation>>(() => {
    try {
      const parsedHours = JSON.parse(hours);
      return Array.isArray(parsedHours) ? parsedHours : [];
    } catch {
      return [];
    }
  }, [hours]);

  const getTimeInMinutes = (time: string): number => {
    const [hoursValue = '0', minutesValue = '0'] = time.split(':');
    return Number(hoursValue) * 60 + Number(minutesValue);
  };

  const isOpen24Hours = (openTime: string, closeTime: string): boolean => {
    const openMinutes = getTimeInMinutes(openTime);
    const closeMinutes = getTimeInMinutes(closeTime);

    return closeMinutes - openMinutes === 24 * 60;
  };

  const sortedTimes = useMemo<Array<LocationTime>>(() => {
    const times: Array<LocationTime> = jsonHours.map(time => ({
      DayOfWeek: time.openDay,
      OpenTime: time.openTime,
      CloseTime: time.closeTime,
      Open24Hours: isOpen24Hours(time.openTime, time.closeTime),
    }));

    const hasSunday = times.some(
      time => time.DayOfWeek.toLowerCase() === 'sunday'
    );

    if (!hasSunday) {
      times.push({
        DayOfWeek: 'Sunday',
        OpenTime: '',
        CloseTime: '',
        Open24Hours: false,
        isClosed: true,
      });
    }

    const hasSaturday = times.some(
      time => time.DayOfWeek.toLowerCase() === 'saturday'
    );

    if (!hasSaturday) {
      times.push({
        DayOfWeek: 'Saturday',
        OpenTime: '',
        CloseTime: '',
        Open24Hours: false,
        isClosed: true,
      });
    }

    return times.sort((a, b) => {
      const firstDayIndex = dayOrder.indexOf(a.DayOfWeek.toLowerCase());
      const secondDayIndex = dayOrder.indexOf(b.DayOfWeek.toLowerCase());

      return firstDayIndex - secondDayIndex;
    });
  }, [jsonHours]);

  const formatDisplayTime = (time: string): string => {
    const formattedTime = moment
      .utc(time, ['H:mm', 'HH:mm'], true)
      .format('h:mm A');

    return formattedTime
      .replace(':00', '')
      .replace('AM', 'a.m.')
      .replace('PM', 'p.m.');
  };

  const getCurrentDay = (): string => {
    if (!currentDate) {
      return '';
    }

    return dayOrder[currentDate.getDay()];
  };

  const getCurrentTimeInMinutes = (): number => {
    if (!currentDate) {
      return 0;
    }

    return currentDate.getHours() * 60 + currentDate.getMinutes();
  };

  const getLocationStatus = (time: LocationTime): LocationStatus => {
    const currentDay = getCurrentDay();

    if (!currentDate || time.DayOfWeek.toLowerCase() !== currentDay) {
      return {
        className: '',
        label: '',
      };
    }

    if (time.isClosed) {
      return {
        className: 'closed',
        label: '',
      };
    }

    if (time.Open24Hours) {
      return {
        className: 'open',
        label: 'Open 24 Hours',
      };
    }

    const currentTime = getCurrentTimeInMinutes();
    const openTime = getTimeInMinutes(time.OpenTime);
    const closeTime = getTimeInMinutes(time.CloseTime);

    if (currentTime >= openTime && currentTime < closeTime) {
      const minutesUntilClosing = closeTime - currentTime;

      if (minutesUntilClosing <= 30) {
        return {
          className: 'closing-soon',
          label: 'Closing Soon',
        };
      }

      return {
        className: 'open',
        label: 'Open Now',
      };
    }

    if (currentTime < openTime && openTime - currentTime <= 30) {
      return {
        className: 'opening-soon',
        label: 'Opening Soon',
      };
    }

    return {
      className: 'closed',
      label: 'Closed',
    };
  };

  const getDisplayedHours = (time: LocationTime): string => {
    if (time.isClosed) {
      return 'Closed';
    }

    if (time.Open24Hours) {
      return 'Open 24 Hours';
    }

    return `${formatDisplayTime(time.OpenTime)} - ${formatDisplayTime(
      time.CloseTime
    )}`;
  };

  useEffect(() => {
    setCurrentDate(new Date());

    const intervalId = window.setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="hours-container">
      <h2>Hours</h2>

      <ul className="hours-list">
        {sortedTimes.map(time => {
          const status = getLocationStatus(time);

          return (
            <li
              className={['hours-item', 'osf-body-copy', status.className]
                .filter(Boolean)
                .join(' ')}
              id={time.DayOfWeek.toLowerCase()}
              key={time.DayOfWeek.toLowerCase()}
            >
              <span className="day">{time.DayOfWeek}</span>

              <span className="time">{getDisplayedHours(time)}</span>

              <span className="status">{status.label}</span>
            </li>
          );
        })}
      </ul>
      <p>Hours may vary during the holidays. Please call to confirm.<br/>
      {hoursNotes}
      </p>
    </div>
  );
};

export default LocationHours;